import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { PropertyInformation } from '../../shared/intelligence/types.js';
import type {
  UsePropertyPublicDataParams,
  UsePropertyPublicDataReturn,
  CachedPropertyData
} from '../types/intelligence';

const CACHE_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days (matches server cache)
const MAX_CACHE_SIZE = 50; // Increased to support browsing multiple properties without excessive re-fetching

// Global client-side cache
// Note: We don't use setTimeout for cache expiration because 30 days (2592000000ms)
// exceeds the 32-bit signed integer limit (~24.8 days) that setTimeout supports.
// Instead, we rely on lazy expiration checks in get() which is more memory-efficient anyway.
class PropertyPublicDataCache {
  private cache = new Map<string, CachedPropertyData>();

  set(key: string, data: CachedPropertyData) {
    this.cache.set(key, data);

    // Maintain cache size limit (LRU eviction)
    if (this.cache.size > MAX_CACHE_SIZE) {
      const entries = Array.from(this.cache.entries());
      entries.sort((a, b) => b[1].timestamp - a[1].timestamp);
      const toDelete = entries.slice(MAX_CACHE_SIZE);
      toDelete.forEach(([cacheKey]) => {
        this.cache.delete(cacheKey);
      });
    }
  }

  get(key: string): CachedPropertyData | undefined {
    const data = this.cache.get(key);
    if (data && Date.now() - data.timestamp < CACHE_DURATION) {
      return data;
    }
    // Auto-cleanup expired data (lazy expiration)
    if (data) {
      this.cache.delete(key);
    }
    return undefined;
  }

  clear() {
    this.cache.clear();
  }
}

const propertyPublicDataCache = new PropertyPublicDataCache();

/**
 * Custom hook for fetching public property data from Rentcast API
 *
 * Features:
 * - Auto-fetches property data when address changes
 * - Client-side caching (30 days) to reduce API calls
 * - Request cancellation on unmount to prevent memory leaks
 * - Handles authentication via AuthContext
 *
 * @param address - Property address to lookup
 * @param enabled - Whether to enable auto-fetching (default: true)
 *
 * @returns {UsePropertyPublicDataReturn}
 * - propertyData: Primary property data (first from array)
 * - allProperties: Full array of matching properties
 * - isLoading: Loading state indicator
 * - error: Error message if fetch failed
 * - isCached: Whether data came from server cache
 * - refetch: Function to manually refetch data
 * - lastFetched: Timestamp of last successful fetch
 */
export function usePropertyPublicData({
  address,
  enabled = true
}: UsePropertyPublicDataParams): UsePropertyPublicDataReturn {
  const { isAuthenticated } = useAuth();
  const [propertyData, setPropertyData] = useState<PropertyInformation | null>(null);
  const [allProperties, setAllProperties] = useState<PropertyInformation[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isCached, setIsCached] = useState<boolean>(false);
  const [lastFetched, setLastFetched] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const mountedRef = useRef<boolean>(true);

  const fetchPropertyData = useCallback(async (showLoading = true) => {
    const trimmedAddress = address?.trim() || '';
    if (!trimmedAddress || !enabled || !isAuthenticated) {
      return;
    }

    // Check client-side cache first
    const cachedData = propertyPublicDataCache.get(trimmedAddress);
    if (cachedData) {
      setPropertyData(cachedData.data[0] || null);
      setAllProperties(cachedData.data);
      setIsCached(cachedData.cached);
      setLastFetched(new Date(cachedData.timestamp).toISOString());
      setError(null);
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (!mountedRef.current) {
      return;
    }

    abortControllerRef.current = new AbortController();

    try {
      if (showLoading) {
        setIsLoading(true);
      }
      setError(null);

      const url = new URL('/api/properties/public', window.location.origin);
      url.searchParams.set('address', trimmedAddress);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        signal: abortControllerRef.current.signal,
      });

      // Handle 404 as valid "no data" scenario, not an error
      if (response.status === 404) {
        if (mountedRef.current) {
          setPropertyData(null);
          setAllProperties(null);
          setError(null);
        }
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        const properties = Array.isArray(result.data) ? result.data : [result.data];

        if (mountedRef.current) {
          setPropertyData(properties[0] || null);
          setAllProperties(properties);
          setIsCached(result.cached || false);
          setLastFetched(new Date().toISOString());
        }

        // Update client-side cache
        propertyPublicDataCache.set(trimmedAddress, {
          data: properties,
          timestamp: Date.now(),
          address: trimmedAddress,
          cached: result.cached || false,
        });
      } else {
        throw new Error(result.error || 'Failed to fetch property public data');
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        return;
      }

      console.error('Error fetching property public data:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      if (mountedRef.current) {
        setError(errorMessage);
        setPropertyData(null);
        setAllProperties(null);
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [address, enabled, isAuthenticated]);

  const refetch = useCallback(async () => {
    await fetchPropertyData(true);
  }, [fetchPropertyData]);

  // Auto-fetch when dependencies change
  useEffect(() => {
    fetchPropertyData();
  }, [fetchPropertyData]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  return {
    propertyData,
    allProperties,
    isLoading,
    error,
    isCached,
    refetch,
    lastFetched,
  };
}
